import {
  type AreaComponent,
  areaComponentSchema,
  CYLINDER_PLACEMENT_MESSAGES,
  componentShapeIssue,
  cylinderPlacementIssue,
  cylinderSurfaceSchema,
  FACE_KEYS,
  type FaceKey,
  flatSurfaceSchema,
  isPlacementInsideSurface,
  MAX_COMPONENTS,
  MAX_PLACEMENTS_PER_FACE,
  MAX_SHAPE_HOLE_POINTS,
  MAX_SHAPE_HOLES,
  MAX_SHAPE_POINTS,
  MIN_SHAPE_POINTS,
  type Placement,
  type PlacementPosition,
  placementSchema,
  type ShapedComponent,
  type Surface,
  surfaceKindMismatch,
} from '@repo/api-contracts';
import { z } from 'zod';
import { koreanMessageOr } from '@/shared/lib';
import { nextAreaName } from '../lib/area-naming';
import { withoutCompositeOperations } from '../lib/composite-asset';
import {
  drawnComponentOf,
  holeRingOf,
  shapeRingLimitOf,
  validateShapeRing,
} from '../lib/drawn-shape';
import { asPolygonComponent, translateHoleRing } from '../lib/hole-ring';
import { followsMeasurementFrame, measurementFrameOf } from '../lib/measurement-drift';
import { normalizeEditedShape } from '../lib/point-edit/normalize-edited-shape';
import { scalePolygonShape } from '../lib/scale-polygon-shape';
import { findInitialPlacementPosition, isPlacementAllowed } from '../lib/surface-mapping';
import type { EditAreaDraftState } from './edit-area-draft-types';

type Commands = Pick<
  EditAreaDraftState,
  | 'upsertComponent'
  | 'removeComponent'
  | 'upsertSurface'
  | 'removeSurface'
  | 'upsertPlacement'
  | 'beginShapeDrawing'
  | 'endShapeDrawing'
  | 'applyDrawnShape'
  | 'applyDrawnHole'
  | 'removeShapeHole'
  | 'moveShapeHole'
  | 'createDrawnArea'
  | 'resizeArea'
  | 'editShapePoints'
  | 'editHolePoints'
  | 'createDefaultArea'
  | 'clearPendingInputs'
  | 'removePlacement'
  | 'removeAreaFromFace'
  | 'copyFace'
  | 'refreshSurfaceRealSize'
>;
interface CommandContext {
  readonly get: () => EditAreaDraftState;
  readonly commit: (patch: Partial<EditAreaDraftState>) => void;
  readonly reject: (message: string) => false;
  readonly canEdit: () => boolean;
  readonly set: (patch: Partial<EditAreaDraftState>) => void;
}

/** 이 화면이 만드는 표면의 판별 파싱 — flat·cylinder 만 받는다. mesh 는 조회·버전 탐색만 한다(ADR-0007 D10). */
/** 기본 인쇄 영역 한 변(mm). 표면이 이보다 작으면 표면의 80% 로 줄인다. */
const DEFAULT_AREA_MM = 50;

const editableSurfaceSchema = z.discriminatedUnion(
  'type',
  [flatSurfaceSchema, cylinderSurfaceSchema],
  { error: '이 화면에서는 평면·원통 표면만 만들 수 있습니다.' },
);

/**
 * 배치를 이 표면에 둘 수 없는 사유. 둘 수 있으면 null 이다. 판정은 저장 계약과 같은 함수로 한다.
 * 원통은 프레임과 가로로 겹치기만 하면 되고(음수 위치·프레임보다 넓은 인쇄 영역 가능, ADR-0007 D9),
 * 거부 문구도 저장 전 확인 목록과 같은 계약 문구(CYLINDER_PLACEMENT_MESSAGES)를 그대로 쓴다.
 * 그 밖의 표면은 실측 범위와 윤곽 안이어야 한다.
 */
const placementRejectionOf = (
  component: ShapedComponent,
  surface: Surface,
  position: PlacementPosition,
): string | null => {
  if (surface.type === 'cylinder') {
    const issue = cylinderPlacementIssue(component, surface.realSize, position);
    return issue === null ? null : CYLINDER_PLACEMENT_MESSAGES[issue];
  }
  if (!isPlacementInsideSurface(component, surface.realSize, position))
    return '인쇄 영역이 표면 실측 범위를 벗어납니다.';
  if (!isPlacementAllowed(component, surface, position))
    return '인쇄 영역이 제품 윤곽을 벗어납니다.';
  return null;
};

/** 참조 무결성을 한 상태 변경으로 유지한다. 실측 수정·갱신은 배치 좌표를 보정하지 않는다. */
export const createEditAreaGeometryCommands = ({
  get,
  commit,
  reject,
  canEdit,
  set,
}: CommandContext): Commands => {
  /** 새 영역과 그 면의 배치를 한 번에 더한다. 표면·배치 거부·면 배치 수를 검사하고 실패하면 null. */
  const addPlacedComponent = (
    face: FaceKey,
    component: AreaComponent,
    surfaceId: string,
    position: PlacementPosition,
  ): string | null => {
    const current = get().faces[face];
    const surface = current?.surfaces.find((value) => value.id === surfaceId);
    if (!current || !surface) {
      reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      return null;
    }
    const rejection = placementRejectionOf(component, surface, position);
    if (rejection !== null) {
      reject(rejection);
      return null;
    }
    if (current.placements.length >= MAX_PLACEMENTS_PER_FACE) {
      reject(`배치는 면마다 최대 ${MAX_PLACEMENTS_PER_FACE}개입니다.`);
      return null;
    }
    commit({
      components: [...get().components, component],
      faces: {
        ...get().faces,
        [face]: {
          ...current,
          placements: [
            ...current.placements,
            { componentId: component.id, surfaceId: surface.id, position },
          ],
        },
      },
    });
    return component.id;
  };

  return {
    upsertComponent: (input) => {
      if (!canEdit()) return false;
      const parsed = areaComponentSchema.safeParse(input);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 값을 확인해 주세요.'),
        );
      const component = parsed.data;
      const shapeIssue = componentShapeIssue(component);
      if (shapeIssue !== null) return reject(shapeIssue);
      const existing = get().components.find((value) => value.id === component.id);
      if (!existing && get().components.length >= MAX_COMPONENTS)
        return reject(`인쇄 영역은 최대 ${MAX_COMPONENTS}개입니다.`);
      if (
        get().components.some((value) => value.id !== component.id && value.name === component.name)
      )
        return reject('인쇄 영역 이름이 중복됩니다.');
      commit({
        components: existing
          ? get().components.map((value) => (value.id === component.id ? component : value))
          : [...get().components, component],
      });
      return true;
    },
    removeComponent: (id) => {
      if (!canEdit()) return false;
      if (!get().components.some((value) => value.id === id))
        return reject('인쇄 영역을 찾을 수 없습니다.');
      const faces = { ...get().faces };
      for (const face of FACE_KEYS) {
        const value = faces[face];
        if (value)
          faces[face] = {
            ...value,
            placements: value.placements.filter((placement) => placement.componentId !== id),
          };
      }
      const removedInputs = new Set<string>(FACE_KEYS.map((face) => `placement:${face}:${id}`));
      if (get().selection.componentId === id) removedInputs.add('component');
      commit({
        components: get().components.filter((value) => value.id !== id),
        faces,
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(([key]) => !removedInputs.has(key)),
        ),
      });
      return true;
    },
    upsertSurface: (face, input) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current) return reject('사용 중인 면을 선택해 주세요.');
      // 실측 종류와 표면 타입은 1:1 이다. 값 검사보다 먼저 서버와 같은 문구로 막는다.
      const mismatch = surfaceKindMismatch(input.type, get().measurementKind);
      if (mismatch !== null) return reject(mismatch);
      const parsed = editableSurfaceSchema.safeParse(input);
      if (!parsed.success)
        return reject(koreanMessageOr(parsed.error.issues[0]?.message, '표면 값을 확인해 주세요.'));
      const surface = parsed.data;
      const existing = current.surfaces.some((value) => value.id === surface.id);
      if (!existing && current.surfaces.length >= 1)
        return reject("면마다 표면은 1개입니다. '다시 추출하기'로 기존 표면을 교체해 주세요.");
      if (
        FACE_KEYS.some(
          (key) =>
            key !== face && get().faces[key]?.surfaces.some((value) => value.id === surface.id),
        )
      )
        return reject('다른 면에서 사용하는 표면 ID입니다.');
      commit({
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            surfaces: existing
              ? current.surfaces.map((value) => (value.id === surface.id ? surface : value))
              : [...current.surfaces, surface],
          },
        },
      });
      return true;
    },
    removeSurface: (face, id) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current?.surfaces.some((value) => value.id === id))
        return reject('표면을 찾을 수 없습니다.');
      const removedInputs = new Set(
        current.placements
          .filter((value) => value.surfaceId === id)
          .map((value) => `placement:${face}:${value.componentId}`),
      );
      if (get().selection.face === face && get().selection.surfaceId === id)
        removedInputs.add('surface');
      commit({
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(([key]) => !removedInputs.has(key)),
        ),
        faces: {
          ...get().faces,
          // 합성 설정은 원통 표면이 있는 면에만 둘 수 있어 표면과 함께 지운다(Q7). 같은 id 로 바꾸는 upsertSurface 는 유지한다.
          [face]: {
            surfaces: current.surfaces.filter((value) => value.id !== id),
            placements: current.placements.filter((value) => value.surfaceId !== id),
          },
        },
        // 합성 설정이 빠지는 면의 진행 중 합성 업로드도 취소한다(D-E) — 끝나도 둘 곳이 없다.
        operations: withoutCompositeOperations(get().operations, face),
      });
      return true;
    },
    upsertPlacement: (face, input) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current) return reject('사용 중인 면을 선택해 주세요.');
      const parsed = placementSchema.safeParse(input);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '배치 위치를 확인해 주세요.'),
        );
      const placement = parsed.data;
      const component = get().components.find((value) => value.id === placement.componentId);
      const surface = current.surfaces.find((value) => value.id === placement.surfaceId);
      if (!component || !surface)
        return reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      const rejection = placementRejectionOf(component, surface, placement.position);
      if (rejection !== null) return reject(rejection);
      const existing = current.placements.some(
        (value) => value.componentId === placement.componentId,
      );
      if (!existing && current.placements.length >= MAX_PLACEMENTS_PER_FACE)
        return reject(`배치는 면마다 최대 ${MAX_PLACEMENTS_PER_FACE}개입니다.`);
      commit({
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            placements: existing
              ? current.placements.map((value) =>
                  value.componentId === placement.componentId ? placement : value,
                )
              : [...current.placements, placement],
          },
        },
      });
      return true;
    },
    beginShapeDrawing: (target) => {
      if (!canEdit()) return false;
      commit({
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(([key]) => key !== 'component'),
        ),
        shapeDrawing: { ...target, mode: target.mode ?? 'outer' },
      });
      return true;
    },
    endShapeDrawing: () => {
      set({ shapeDrawing: null });
    },
    applyDrawnShape: (face, input) => {
      if (!canEdit()) return false;
      const ringIssue = validateShapeRing(input.points);
      if (ringIssue !== null) return reject(ringIssue);
      const name = input.name.trim();
      const { component, position } = drawnComponentOf(input.points, {
        id: input.componentId ?? crypto.randomUUID(),
        name,
      });
      const { components } = get();
      const existing = components.some((value) => value.id === component.id);
      if (components.some((value) => value.id !== component.id && value.name === name))
        return reject('인쇄 영역 이름이 중복됩니다.');
      if (!existing && components.length >= MAX_COMPONENTS)
        return reject(`인쇄 영역은 최대 ${MAX_COMPONENTS}개입니다.`);
      const parsed = areaComponentSchema.safeParse(component);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 값을 확인해 주세요.'),
        );
      const shapeIssue = componentShapeIssue(parsed.data);
      if (shapeIssue !== null) return reject(shapeIssue);
      const current = get().faces[face];
      const surface = current?.surfaces.find((value) => value.id === input.surfaceId);
      if (!current || !surface)
        return reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      const rejection = placementRejectionOf(parsed.data, surface, position);
      if (rejection !== null) return reject(rejection);
      const placement: Placement = { componentId: component.id, surfaceId: surface.id, position };
      const placed = current.placements.some((value) => value.componentId === component.id);
      if (!placed && current.placements.length >= MAX_PLACEMENTS_PER_FACE)
        return reject(`배치는 면마다 최대 ${MAX_PLACEMENTS_PER_FACE}개입니다.`);
      commit({
        components: existing
          ? components.map((value) => (value.id === component.id ? parsed.data : value))
          : [...components, parsed.data],
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            placements: placed
              ? current.placements.map((value) =>
                  value.componentId === component.id ? placement : value,
                )
              : [...current.placements, placement],
          },
        },
      });
      return true;
    },
    applyDrawnHole: (face, input) => {
      if (!canEdit()) return false;
      const ringIssue = validateShapeRing(input.points, 'hole');
      if (ringIssue !== null) return reject(ringIssue);
      const component = get().components.find((value) => value.id === input.componentId);
      if (!component) return reject('인쇄 제외 영역을 지정할 인쇄 영역을 찾을 수 없습니다.');
      const current = get().faces[face];
      const placement = current?.placements.find((value) => value.componentId === component.id);
      if (!placement) return reject('이 면에 배치한 뒤 인쇄 제외 영역을 지정할 수 있습니다.');
      if (placement.surfaceId !== input.surfaceId)
        return reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      // 사각형·원형은 평면 위에서만 같은 모양의 자유 다각형으로 바꿔 구멍을 받는다(원통은 점 편집을 하지 않는다).
      const surface = current?.surfaces.find((value) => value.id === placement.surfaceId);
      if (component.shape?.type !== 'polygon' && surface?.type !== 'flat')
        return reject(
          '원통 표면에서는 자유 다각형 인쇄 영역에만 인쇄 제외 영역을 지정할 수 있습니다.',
        );
      const polygon = asPolygonComponent(component);
      if (polygon.shape?.type !== 'polygon')
        return reject('인쇄 제외 영역을 지정할 수 없는 모양입니다.');
      const holes = polygon.shape.holes ?? [];
      if (holes.length >= MAX_SHAPE_HOLES)
        return reject(`인쇄 제외 영역은 최대 ${MAX_SHAPE_HOLES}개입니다.`);
      const candidate: AreaComponent = {
        ...polygon,
        shape: {
          ...polygon.shape,
          holes: [...holes, holeRingOf(input.points, placement.position)],
        },
      };
      const shapeIssue = componentShapeIssue(candidate);
      if (shapeIssue !== null) return reject(shapeIssue);
      // 사각형·원형을 처음 자유 다각형으로 바꾸면 바깥 고리가 달라진다(원형은 64점 근사 → 곡선 손잡이 4점) — 그 고리로
      // 표면 실측·제품 윤곽 안인지 다시 본다. 이미 자유 다각형이면 바깥 고리가 그대로라 같은 결과다.
      if (surface !== undefined) {
        const rejection = placementRejectionOf(candidate, surface, placement.position);
        if (rejection !== null) return reject(rejection);
      }
      const parsed = areaComponentSchema.safeParse(candidate);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 제외 영역을 확인해 주세요.'),
        );
      commit({
        components: get().components.map((value) =>
          value.id === candidate.id ? parsed.data : value,
        ),
        selection: { ...get().selection, holeIndex: holes.length },
      });
      return true;
    },
    removeShapeHole: (componentId, index) => {
      if (!canEdit()) return false;
      const component = get().components.find((value) => value.id === componentId);
      const shape = component?.shape;
      if (!component || shape?.type !== 'polygon' || !shape.holes?.[index])
        return reject('지울 인쇄 제외 영역이 없습니다.');
      const remaining = shape.holes.filter((_, position) => position !== index);
      const { holes: _removed, ...rest } = shape;
      const next: AreaComponent = {
        ...component,
        shape: remaining.length === 0 ? rest : { ...rest, holes: remaining },
      };
      commit({
        components: get().components.map((value) => (value.id === componentId ? next : value)),
        selection: { ...get().selection, holeIndex: null },
      });
      return true;
    },
    moveShapeHole: (face, componentId, index, dxMm, dyMm) => {
      if (!canEdit()) return false;
      const component = get().components.find((value) => value.id === componentId);
      const shape = component?.shape;
      const hole = shape?.type === 'polygon' ? shape.holes?.[index] : undefined;
      if (!component || shape?.type !== 'polygon' || hole === undefined)
        return reject('옮길 인쇄 제외 영역이 없습니다.');
      if (!get().faces[face]?.placements.some((value) => value.componentId === componentId))
        return reject('이 면에 배치된 인쇄 영역이 아닙니다.');
      const candidate: AreaComponent = {
        ...component,
        shape: {
          ...shape,
          holes: (shape.holes ?? []).map((value, position) =>
            position === index ? translateHoleRing(hole, dxMm, dyMm) : value,
          ),
        },
      };
      // 모양 검사가 먼저다 — 바깥으로 밀려 음수가 된 점도 계약 파싱 문구 대신 '바깥을 벗어납니다'로 알린다.
      const shapeIssue = componentShapeIssue(candidate);
      if (shapeIssue !== null) return reject(shapeIssue);
      const parsed = areaComponentSchema.safeParse(candidate);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 제외 영역을 확인해 주세요.'),
        );
      commit({
        components: get().components.map((value) =>
          value.id === componentId ? parsed.data : value,
        ),
      });
      return true;
    },
    copyFace: (source, target, { includeSurface }) => {
      if (!canEdit()) return false;
      if (source === target) return reject('같은 면에는 가져올 수 없습니다.');
      const from = get().faces[source];
      const to = get().faces[target];
      if (!from || !to) return reject('사용 중인 면을 선택해 주세요.');
      if (from.placements.length === 0) return reject('가져올 면에 배치가 없습니다.');
      const sourceSurface = from.surfaces[0];
      if (includeSurface && sourceSurface === undefined)
        return reject('가져올 면에 표면이 없습니다.');
      // 복사본은 계약 파싱으로 만든다 — 깊은 복사이면서 flat·cylinder 만 통과한다(mesh 정의는 편집 자체가 막혀 있다).
      const copiedSurface =
        includeSurface && sourceSurface !== undefined
          ? editableSurfaceSchema.safeParse({
              ...sourceSurface,
              id: to.surfaces[0]?.id ?? crypto.randomUUID(),
            })
          : null;
      if (copiedSurface !== null && !copiedSurface.success)
        return reject('가져올 면의 표면을 이 화면에서 복사할 수 없습니다.');
      const targetSurface: Surface | undefined = copiedSurface?.data ?? to.surfaces[0];
      if (targetSurface === undefined)
        return reject("이 면에 표면이 없습니다. 먼저 '상품 추출하기'로 표면을 만들어 주세요.");
      const copied: Placement[] = [];
      for (const item of from.placements) {
        const component = get().components.find((value) => value.id === item.componentId);
        if (!component) return reject('가져올 배치의 인쇄 영역을 찾을 수 없습니다.');
        const rejection = placementRejectionOf(component, targetSurface, item.position);
        if (rejection !== null)
          return reject(`'${component.name}' 배치를 이 면에 둘 수 없습니다 — ${rejection}`);
        copied.push({
          componentId: item.componentId,
          surfaceId: targetSurface.id,
          position: { xMm: item.position.xMm, yMm: item.position.yMm },
        });
      }
      const prefix = `placement:${target}:`;
      const surfaceInputStale = includeSurface && get().selection.face === target;
      commit({
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(
            ([key]) => !key.startsWith(prefix) && !(surfaceInputStale && key === 'surface'),
          ),
        ),
        faces: {
          ...get().faces,
          [target]: { ...to, surfaces: [targetSurface], placements: copied },
        },
      });
      return true;
    },
    createDrawnArea: (face, input) => {
      if (!canEdit()) return null;
      const { components } = get();
      const names = components.map((value) => value.name);
      const parsed = areaComponentSchema.safeParse({
        id: crypto.randomUUID(),
        name: nextAreaName(input.kind === 'rect' ? '인쇄 영역' : '원형', names),
        widthMm: input.rect.widthMm,
        heightMm: input.rect.heightMm,
        ...(input.kind === 'ellipse' ? { shape: { type: 'ellipse' } } : {}),
      });
      if (!parsed.success) {
        reject(koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 값을 확인해 주세요.'));
        return null;
      }
      if (components.length >= MAX_COMPONENTS) {
        reject(`인쇄 영역은 최대 ${MAX_COMPONENTS}개입니다.`);
        return null;
      }
      return addPlacedComponent(face, parsed.data, input.surfaceId, {
        xMm: input.rect.xMm,
        yMm: input.rect.yMm,
      });
    },
    resizeArea: (face, componentId, rect) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      const component = get().components.find((value) => value.id === componentId);
      const placement = current?.placements.find((value) => value.componentId === componentId);
      if (!current || !component || !placement)
        return reject('이 면에 배치된 인쇄 영역이 아닙니다.');
      const { shape } = component;
      const resized = {
        id: component.id,
        name: component.name,
        widthMm: rect.widthMm,
        heightMm: rect.heightMm,
        ...(shape?.type === 'polygon'
          ? { shape: scalePolygonShape(shape, component, rect) }
          : shape
            ? { shape }
            : {}),
      };
      const parsed = areaComponentSchema.safeParse(resized);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 값을 확인해 주세요.'),
        );
      const shapeIssue = componentShapeIssue(parsed.data);
      if (shapeIssue !== null) return reject(shapeIssue);
      const surface = current.surfaces.find((value) => value.id === placement.surfaceId);
      if (!surface) return reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      const position = { xMm: rect.xMm, yMm: rect.yMm };
      const rejection = placementRejectionOf(parsed.data, surface, position);
      if (rejection !== null) return reject(rejection);
      commit({
        components: get().components.map((value) =>
          value.id === componentId ? parsed.data : value,
        ),
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            placements: current.placements.map((value) =>
              value.componentId === componentId ? { ...value, position } : value,
            ),
          },
        },
      });
      return true;
    },
    editShapePoints: (face, componentId, points) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      const component = get().components.find((value) => value.id === componentId);
      const placement = current?.placements.find((value) => value.componentId === componentId);
      if (!current || !component || !placement)
        return reject('이 면에 배치된 인쇄 영역이 아닙니다.');
      if (points.length < MIN_SHAPE_POINTS)
        return reject(`인쇄 영역 점은 ${MIN_SHAPE_POINTS}개 이상이어야 합니다.`);
      if (points.length > MAX_SHAPE_POINTS) return reject(shapeRingLimitOf('area').message);
      const surface = current.surfaces.find((value) => value.id === placement.surfaceId);
      if (!surface) return reject('같은 면의 표면과 존재하는 인쇄 영역을 선택해 주세요.');
      const holes = component.shape?.type === 'polygon' ? component.shape.holes : undefined;
      const edited = normalizeEditedShape(points, holes, placement.position);
      if (edited === null) return reject('인쇄 영역 모양을 확인해 주세요.');
      const parsed = areaComponentSchema.safeParse({
        id: component.id,
        name: component.name,
        widthMm: edited.widthMm,
        heightMm: edited.heightMm,
        shape: edited.shape,
      });
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 모양을 확인해 주세요.'),
        );
      const shapeIssue = componentShapeIssue(parsed.data);
      if (shapeIssue !== null) return reject(shapeIssue);
      const rejection = placementRejectionOf(parsed.data, surface, edited.position);
      if (rejection !== null) return reject(rejection);
      commit({
        components: get().components.map((value) =>
          value.id === componentId ? parsed.data : value,
        ),
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            placements: current.placements.map((value) =>
              value.componentId === componentId ? { ...value, position: edited.position } : value,
            ),
          },
        },
      });
      return true;
    },
    editHolePoints: (face, componentId, index, points) => {
      if (!canEdit()) return false;
      const component = get().components.find((value) => value.id === componentId);
      const shape = component?.shape;
      const hole = shape?.type === 'polygon' ? shape.holes?.[index] : undefined;
      if (!component || shape?.type !== 'polygon' || hole === undefined)
        return reject('고칠 인쇄 제외 영역이 없습니다.');
      const placement = get().faces[face]?.placements.find(
        (value) => value.componentId === componentId,
      );
      if (!placement) return reject('이 면에 배치된 인쇄 영역이 아닙니다.');
      if (points.length < MIN_SHAPE_POINTS)
        return reject(`인쇄 제외 영역 점은 ${MIN_SHAPE_POINTS}개 이상이어야 합니다.`);
      if (points.length > MAX_SHAPE_HOLE_POINTS) return reject(shapeRingLimitOf('hole').message);
      const candidate: AreaComponent = {
        ...component,
        shape: {
          ...shape,
          holes: (shape.holes ?? []).map((value, position) =>
            position === index ? holeRingOf(points, placement.position) : value,
          ),
        },
      };
      // 모양 검사가 먼저다 — 바깥으로 나가 음수가 된 점도 계약 파싱 문구 대신 '바깥을 벗어납니다'로 알린다.
      const shapeIssue = componentShapeIssue(candidate);
      if (shapeIssue !== null) return reject(shapeIssue);
      const parsed = areaComponentSchema.safeParse(candidate);
      if (!parsed.success)
        return reject(
          koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 제외 영역을 확인해 주세요.'),
        );
      commit({
        components: get().components.map((value) =>
          value.id === componentId ? parsed.data : value,
        ),
      });
      return true;
    },
    createDefaultArea: (face, kind = 'rect') => {
      if (!canEdit()) return null;
      const surface = get().faces[face]?.surfaces[0];
      if (!surface) {
        reject('표면을 먼저 추출해 주세요.');
        return null;
      }
      const { components } = get();
      const fit = (size: number): number =>
        size < DEFAULT_AREA_MM ? Math.max(1, Math.floor(size * 0.8)) : DEFAULT_AREA_MM;
      const size = {
        widthMm: fit(surface.realSize.widthMm),
        heightMm: fit(surface.realSize.heightMm),
      };
      const position = findInitialPlacementPosition(size, surface);
      if (position === null) {
        reject('인쇄 영역이 들어갈 자리가 없습니다. 표면 크기와 윤곽을 확인해 주세요.');
        return null;
      }
      const parsed = areaComponentSchema.safeParse({
        id: crypto.randomUUID(),
        name: nextAreaName(
          kind === 'rect' ? '인쇄 영역' : '원형',
          components.map((value) => value.name),
        ),
        ...size,
        ...(kind === 'ellipse' ? { shape: { type: 'ellipse' } } : {}),
      });
      if (!parsed.success) {
        reject(koreanMessageOr(parsed.error.issues[0]?.message, '인쇄 영역 값을 확인해 주세요.'));
        return null;
      }
      if (components.length >= MAX_COMPONENTS) {
        reject(`인쇄 영역은 최대 ${MAX_COMPONENTS}개입니다.`);
        return null;
      }
      return addPlacedComponent(face, parsed.data, surface.id, position);
    },
    clearPendingInputs: () => {
      commit({ pendingInputs: {} });
    },
    removePlacement: (face, componentId) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current?.placements.some((value) => value.componentId === componentId))
        return reject('배치를 찾을 수 없습니다.');
      commit({
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(
            ([key]) => key !== `placement:${face}:${componentId}`,
          ),
        ),
        faces: {
          ...get().faces,
          [face]: {
            ...current,
            placements: current.placements.filter((value) => value.componentId !== componentId),
          },
        },
      });
      return true;
    },
    removeAreaFromFace: (face, componentId) => {
      if (!canEdit()) return false;
      const current = get().faces[face];
      if (!current?.placements.some((value) => value.componentId === componentId))
        return reject('배치를 찾을 수 없습니다.');
      const faces = {
        ...get().faces,
        [face]: {
          ...current,
          placements: current.placements.filter((value) => value.componentId !== componentId),
        },
      };
      // 비활성 면에 남은 배치도 참조로 친다 — 어디에서든 쓰이면 인쇄 영역은 남긴다.
      const placedElsewhere = FACE_KEYS.some(
        (key) =>
          key !== face &&
          (faces[key]?.placements.some((value) => value.componentId === componentId) ?? false),
      );
      const removedInputs = new Set<string>(
        placedElsewhere
          ? [`placement:${face}:${componentId}`]
          : FACE_KEYS.map((key) => `placement:${key}:${componentId}`),
      );
      if (!placedElsewhere && get().selection.componentId === componentId)
        removedInputs.add('component');
      commit({
        ...(placedElsewhere
          ? {}
          : { components: get().components.filter((value) => value.id !== componentId) }),
        faces,
        pendingInputs: Object.fromEntries(
          Object.entries(get().pendingInputs).filter(([key]) => !removedInputs.has(key)),
        ),
      });
      return true;
    },
    refreshSurfaceRealSize: () => {
      if (!canEdit()) return false;
      const { measurement, measurementKind } = get();
      if (measurement === null) return reject('상품 실측을 먼저 등록해 주세요.');
      // 원통은 계약 함수가 만든 프레임 폭 double 을 그대로 넣는다 — 서버 실측 검사와 같은 값이어야 한다.
      const frame = measurementFrameOf(measurementKind, measurement);
      const faces = { ...get().faces };
      for (const face of FACE_KEYS) {
        const value = faces[face];
        if (value)
          faces[face] = {
            ...value,
            surfaces: value.surfaces.map((surface) =>
              followsMeasurementFrame(surface.type, measurementKind)
                ? { ...surface, realSize: { ...frame } }
                : surface,
            ),
          };
      }
      commit({ faces });
      return true;
    },
  };
};
